import { useState, useRef, useEffect } from "react";
import StaggerChart from "./StaggerChart";
import SectionHeader from "./SectionHeader";
import RevenuePlanner from "../revenue-planner/RevenuePlanner";
import CohortRevenueStack from "./CohortRevenueStack";
import { useCohortRevenueStack } from "../../hooks/useCohortRevenueStack";
import type { ForecastData } from "../../hooks/useForecastData";

// =============================================================================
// FORECAST TAB — Container for Stagger Chart + Revenue Planner
// =============================================================================
// "Past" section: StaggerChart (forecast evolution over months)
// "Future" section: RevenuePlanner (bidirectional goal planner with source toggles)
//
// Replaces entire dashboard view when "Forecast & Planning" tab is active.
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// Stagger Chart Info Panel
// ─────────────────────────────────────────────────────────────────────────────

function StaggerInfoPanel({ onClose }: { onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="liquid-glass rounded-[14px]"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        marginTop: 8,
        padding: "24px 28px",
        zIndex: 20,
        animation: "fadeIn 0.15s ease-out",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 rounded-md transition-colors"
        style={{
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-tertiary)",
          border: "1px solid var(--border)",
          background: "var(--bg)",
          cursor: "pointer",
          fontSize: 14,
        }}
      >
        ✕
      </button>

      {/* Title */}
      <h4
        className="font-semibold"
        style={{ fontSize: 14, color: "var(--text-primary)", marginBottom: 4 }}
      >
        How to Read This Chart
      </h4>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 20, lineHeight: 1.5 }}>
        This is a Forecast Evolution chart — the same forecasting method used by Fortune 500
        companies to measure how accurate their revenue predictions are over time.
      </p>

      {/* Two-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Left: How to read */}
        <div>
          <p
            className="font-semibold uppercase tracking-wide"
            style={{
              fontSize: 10,
              color: "var(--text-tertiary)",
              letterSpacing: "0.08em",
              marginBottom: 10,
            }}
          >
            Reading the Table
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <p
                className="font-medium"
                style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 2 }}
              >
                Each row = when the forecast was made
              </p>
              <p style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
                "Nov '25" row means "this is what we predicted back in November."
              </p>
            </div>
            <div>
              <p
                className="font-medium"
                style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 2 }}
              >
                Each column = the month being predicted
              </p>
              <p style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
                The "Jan '26" column shows every prediction anyone ever made about January's
                numbers.
              </p>
            </div>
            <div>
              <p
                className="font-medium"
                style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 2 }}
              >
                Read down a column to see the story
              </p>
              <p style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
                Early predictions are at the top, later (more accurate) ones below. The bottom of
                each column is the actual result.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Symbols & colors */}
        <div>
          <p
            className="font-semibold uppercase tracking-wide"
            style={{
              fontSize: 10,
              color: "var(--text-tertiary)",
              letterSpacing: "0.08em",
              marginBottom: 10,
            }}
          >
            Symbols &amp; Colors
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                className="font-bold flex-shrink-0"
                style={{
                  fontSize: 13,
                  color: "var(--accent)",
                  width: 24,
                  textAlign: "center",
                }}
              >
                *
              </span>
              <div>
                <p
                  className="font-medium"
                  style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 1 }}
                >
                  Actual confirmed number
                </p>
                <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  Not a prediction — this is what really happened.
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                className="flex-shrink-0 rounded"
                style={{
                  width: 24,
                  height: 16,
                  marginTop: 2,
                  background: "color-mix(in srgb, var(--green) 15%, transparent)",
                  border: "1px solid var(--green)",
                }}
              />
              <div>
                <p
                  className="font-medium"
                  style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 1 }}
                >
                  Beat the forecast
                </p>
                <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  Actual result was higher than predicted.
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                className="flex-shrink-0 rounded"
                style={{
                  width: 24,
                  height: 16,
                  marginTop: 2,
                  background: "color-mix(in srgb, var(--red) 15%, transparent)",
                  border: "1px solid var(--red)",
                }}
              />
              <div>
                <p
                  className="font-medium"
                  style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 1 }}
                >
                  Missed the forecast
                </p>
                <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  Actual result was lower than predicted.
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                className="flex-shrink-0"
                style={{
                  width: 24,
                  fontSize: 13,
                  color: "var(--text-tertiary)",
                  textAlign: "center",
                }}
              >
                —
              </span>
              <div>
                <p
                  className="font-medium"
                  style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 1 }}
                >
                  No forecast existed yet
                </p>
                <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  That month hadn't been predicted at that point in time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom: Why it matters */}
      <div
        className="rounded-lg"
        style={{
          marginTop: 20,
          padding: "14px 16px",
          background: "color-mix(in srgb, var(--accent) 4%, transparent)",
          border: "1px solid color-mix(in srgb, var(--accent) 12%, transparent)",
        }}
      >
        <p
          className="font-medium"
          style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 4 }}
        >
          Why this matters
        </p>
        <p style={{ fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.6 }}>
          Most dashboards only show "you made $X this month." This chart answers a deeper question:{" "}
          <strong style={{ color: "var(--text-primary)" }}>
            are we getting better at predicting?
          </strong>{" "}
          If early forecasts are way off but later ones get close, the team is learning. If
          forecasts are always wrong, something in the pipeline is unpredictable — and that's worth
          investigating.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ForecastTabProps {
  forecastData: ForecastData;
  kpis: {
    aov: number;
    closeRate: number;
    showUpRate: number;
    cashPerCallTaken: number;
  };
  organizationId?: string | null;
  canEdit?: boolean;
}

export default function ForecastTab({
  forecastData,
  organizationId,
  canEdit = true,
}: ForecastTabProps) {
  const [staggerMetric, setStaggerMetric] = useState<"revenue_closed" | "cash_collected">(
    "cash_collected"
  );
  const [showStaggerInfo, setShowStaggerInfo] = useState(false);

  // Cohort revenue stack data — fetched inside ForecastTab since it's forecast-only
  const { data: cohortData, isLoading: cohortLoading } = useCohortRevenueStack(organizationId);

  if (forecastData.isLoading) {
    return (
      <div
        className="liquid-glass rounded-[14px] flex items-center justify-center"
        style={{ padding: "60px", minHeight: 300 }}
      >
        <span className="text-glass-muted text-sm">Loading forecast data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {/* 1. Cash Planner — the main action area */}
      <SectionHeader title="Cash Planner">
        <RevenuePlanner
          organizationId={organizationId}
          currentMonthMetrics={forecastData.currentMonthMetrics}
          canEdit={canEdit}
        />
      </SectionHeader>

      {/* 2. Cohort Revenue Stack: Cash by entry-month cohort */}
      {cohortLoading ? (
        <div
          className="liquid-glass p-4 flex items-center justify-center"
          style={{ minHeight: 120 }}
        >
          <span className="text-glass-muted text-sm">Loading cohort data...</span>
        </div>
      ) : (
        <CohortRevenueStack data={cohortData} />
      )}

      {/* 3. Forecast Evolution — historical prediction accuracy (bottom) */}
      <div style={{ position: "relative" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3
              className="font-semibold tracking-wide uppercase"
              style={{ fontSize: 11, color: "var(--text-secondary)", letterSpacing: "0.08em" }}
            >
              Forecast Evolution
            </h3>
            <button
              onClick={() => setShowStaggerInfo(!showStaggerInfo)}
              title="How to read this chart"
              className="rounded-full transition-colors"
              style={{
                width: 18,
                height: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                color: showStaggerInfo ? "var(--accent)" : "var(--text-tertiary)",
                border: `1px solid ${showStaggerInfo ? "var(--accent)" : "var(--border)"}`,
                background: showStaggerInfo
                  ? "color-mix(in srgb, var(--accent) 8%, transparent)"
                  : "transparent",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ?
            </button>
          </div>
          <div
            className="flex rounded-full overflow-hidden"
            style={{
              border: "1px solid var(--border)",
              background: "var(--bg)",
              padding: 2,
            }}
          >
            {[
              { key: "cash_collected" as const, label: "Cash", activeColor: "var(--accent)" },
              { key: "revenue_closed" as const, label: "Revenue", activeColor: "var(--red)" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setStaggerMetric(opt.key)}
                className="font-semibold transition-all duration-200 rounded-full anim-tap"
                style={{
                  fontSize: 11,
                  padding: "5px 14px",
                  color: staggerMetric === opt.key ? opt.activeColor : "var(--text-tertiary)",
                  background: staggerMetric === opt.key ? "var(--surface)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: staggerMetric === opt.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <StaggerChart
          stagger={forecastData.stagger}
          metric={staggerMetric}
          activeColor={staggerMetric === "revenue_closed" ? "var(--red)" : "var(--accent)"}
        />
        {showStaggerInfo && <StaggerInfoPanel onClose={() => setShowStaggerInfo(false)} />}
      </div>
    </div>
  );
}
